import { declined, RuleError, type Declined } from './ruleError';
import { assemblyOf, toolPlaced, type Fitting } from './assemblyStore';
import type { Vec3 } from './assemblyPose';
import type { AssemblyView } from './assemblyScene';
import { shapeEdits } from './fittingModels';
import { fitAxis, loadFit } from './holeFit';
import { loadDeps } from './meshScene';
import { builtBoundsOf } from './openaiBlenderApi';
import { makesHardware, type SubStepKind } from './subStepKinds';
import { madeOf, meshSrc, meshStamp, pieceOf, type Job3D, type PiecePart } from './threeDApi';
import { MM_PER_TRUE_UNIT } from './meshAnnotations';

// TWO PIECES OF HARDWARE BUILT TOGETHER, FITTED AS THEY WERE BUILT.
//
// A hardware set is modelled as one object, its pieces fitted together as in use, and kept as
// one model per piece — each still carrying what Blender measured of it in the SET's frame (see
// _keep_pieces). So where a spring washer ends up on its screw is not a question: the set is the
// answer, to the tenth of a millimetre. Asking a model to read it off a diagram was asking for
// an estimate of something already known, and the estimate put the washer on top of the head.
//
// ONLY FOR A SUB-STEP THAT COMBINES TWO PIECES OF HARDWARE, and only when a kept set holds both
// (see setPlacement). Nothing is ever built together with a component, so a screw going into a
// rail has no such answer and keeps the model's reading.

export type SetPlacement = {
  // The run both were cut from — the number the strip shows under each.
  of: number;
  // Where the moving part's centre rests, from the fixed part's centre, in millimetres.
  seat: Vec3;
  // Each part's measured size, for working out how far out a movement has to start.
  movingSize: Vec3;
  targetSize: Vec3;
  // The two kept models, for the shaft's axis (see approachFromSet).
  moving: Job3D;
  target: Job3D;
};

const centre = (b: { min: number[]; max: number[] }): Vec3 =>
  [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
const size = (b: { min: number[]; max: number[] }): Vec3 =>
  [b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]];

// Null for another kind of sub-step. Declined, and why, where the set does not answer: a part with
// no model, not cut from a set, with no measures, or turned or rebuilt since the cut — its frame
// is then no longer the set's, and the set's numbers would place it wrong. `names`: the two parts
// as the page names them, for saying so.
//
// TWO PARTS CUT FROM DIFFERENT SETS are placed by the MOVING part's set, where it holds the fixed
// part too: a spring washer kept from the set that builds it on its screw, and the screw kept
// from the one that builds it with its hex key. That set says where the washer sits on ITS
// screw; carried over to the kept screw, from centre to centre, it is where it sits on this one.
// Only where the two screws are the same size on every axis, to the tenth of a millimetre a set
// measures in: the same part, standing the same way in both frames. Anything else is refused
// rather than stretched — and so is a fixed part the moving one's set does not hold. `sets`: the
// manual's set runs (see useManualSets), which carry each part's box in their own frame.
const SAME_MM = 0.15;
// THE SAME PART STANDING THE SAME WAY in two frames: one size on every axis, to that tenth.
export const sameSize = (a: { min: number[]; max: number[] }, b: { min: number[]; max: number[] }) => {
  const [sa, sb] = [size(a), size(b)];
  return sa.every((v, i) => Math.abs(v - sb[i]) <= SAME_MM);
};
export function setPlacement(
  kind: SubStepKind | null | undefined,
  moving: Job3D | null | undefined,
  target: Job3D | null | undefined,
  sets: Job3D[],
  names: { moving: string; target: string },
): SetPlacement | Declined | null {
  if (!makesHardware(kind)) return null;
  if (!moving || !target) return declined('notice.rule.set.noModel', { part: !moving ? names.moving : names.target });
  const pm = pieceOf(moving);
  const pt = pieceOf(target);
  if (pm?.from !== 'set' || pt?.from !== 'set') {
    return declined('notice.rule.set.notCut', { part: pm?.from !== 'set' ? names.moving : names.target });
  }
  if (shapeEdits(moving) > 0 || shapeEdits(target) > 0) {
    return declined('notice.rule.set.edited', { part: shapeEdits(moving) > 0 ? names.moving : names.target });
  }
  const bm = builtBoundsOf(moving);
  const bt = builtBoundsOf(target);
  if (!bm || !bt) return declined('notice.rule.set.noBox', { part: !bm ? names.moving : names.target });
  // WHERE THE FIXED PART STANDS IN THE MOVING PART'S FRAME: its own box, where both were cut from
  // one set; the moving part's set's box for the same part, where they were not.
  const br = pm.of === pt.of ? bt : boxInSet(sets.find((j) => j.id === pm.of), target);
  if (!br) return declined('notice.rule.set.notInSet', { part: names.moving, onto: names.target });
  const ts = size(bt);
  if (!sameSize(br, bt)) {
    return declined('notice.rule.set.otherSize', { part: names.moving, onto: names.target });
  }
  const cm = centre(bm);
  const cr = centre(br);
  return {
    of: pm.of,
    seat: [cm[0] - cr[0], cm[1] - cr[1], cm[2] - cr[2]],
    movingSize: size(bm),
    targetSize: ts,
    moving,
    target,
  };
}

// THE BOX A SET GIVES ONE OF ITS PARTS, by the part's catalog id: the set lists its parts by id
// and name (params.set.parts), and Blender measured each by that name (params.measures). Null
// where the set does not hold the part, or holds it under a name measured more than once.
export function boxInSet(set: Job3D | undefined, part: Job3D): { min: number[]; max: number[] } | null {
  if (!set || part.itemId == null) return null;
  const parts = ((set.params?.set as { parts?: { name?: string; itemId?: number }[] } | undefined)?.parts) ?? [];
  const name = parts.find((p) => p.itemId === part.itemId)?.name;
  const measures = Array.isArray(set.params?.measures)
    ? set.params.measures as { name?: string; min?: number[]; max?: number[] }[] : [];
  const hits = measures.filter((m) => name && m.name === name && m.min?.length === 3 && m.max?.length === 3);
  return hits.length === 1 ? { min: hits[0].min!, max: hits[0].max! } : null;
}

// Room between the two at the start — the margin a screw starts outside its hole by (see
// holeMovement), so the movement is seen beginning apart rather than touching.
const CLEAR_MM = 3;
// Enough moments to catch a part passing through another, and few enough that the two ends
// are measured in a second.
const MOMENTS = Array.from({ length: 21 }, (_, i) => i / 20);

const travelAlong = (p: SetPlacement, a: Vec3): number => {
  const i = a.findIndex((c) => c !== 0);
  const out = p.seat[i] * a[i];
  return Math.max(CLEAR_MM, p.targetSize[i] / 2 + p.movingSize[i] / 2 + CLEAR_MM - out);
};

const flip = (v: Vec3): Vec3 => [-v[0], -v[1], -v[2]];

// A POINT OF A PART'S OWN FRAME (millimetres) WHERE THE PIECE STANDS IT: `m` takes the part's file —
// its millimetres over MM_PER_TRUE_UNIT — to the piece's millimetres. A direction leaves out the move.
const throughPart = (m: number[], v: Vec3, direction = false): Vec3 => {
  const [x, y, z] = v.map((c) => c / MM_PER_TRUE_UNIT);
  const w = direction ? 0 : 1;
  return [0, 1, 2].map((i) => m[i] * x + m[4 + i] * y + m[8 + i] * z + m[12 + i] * w) as unknown as Vec3;
};

function rideOnPiece(
  f: Pick<Fitting, 'toolSeat' | 'toolSpin'>,
  parts: PiecePart[],
  tool: Job3D,
  sets: Job3D[],
  jobOf: (id: number) => Job3D | undefined,
): Vec3 | null {
  const jobs = parts.map((p) => jobOf(p.job));
  if (jobs.some((j) => !j || !builtBoundsOf(j))) return null;
  // The part the tool works: the one a set built it with.
  const k = jobs.findIndex((j) => sets.some((s) => boxInSet(s, j!) && boxInSet(s, tool)));
  if (k < 0) return null;
  const onPart = toolRideOf(f, jobs[k], tool, sets);
  if (!onPart) return null;
  // Where the tool's middle stands in the piece, and the piece's own middle: its parts' boxes, as
  // the piece stands them.
  const own = builtBoundsOf(jobs[k])!;
  const c = centre(own);
  const at = throughPart(parts[k].m, c);
  const turned = throughPart(parts[k].m, onPart, true);
  const corners = parts.flatMap((p, i) => {
    const b = builtBoundsOf(jobs[i])!;
    return [0, 1, 2, 3, 4, 5, 6, 7].map((n) => throughPart(p.m, [
      n & 1 ? b.max[0] : b.min[0], n & 2 ? b.max[1] : b.min[1], n & 4 ? b.max[2] : b.min[2],
    ]));
  });
  const middle = [0, 1, 2].map((i) => (Math.min(...corners.map((q) => q[i])) + Math.max(...corners.map((q) => q[i]))) / 2);
  return [0, 1, 2].map((i) => at[i] + turned[i] - middle[i]) as unknown as Vec3;
}

// THE SHAFT'S AXIS, from head to tip, read off its own mesh in the set's frame — the same reading
// that stands a screw in its hole (see fitAxis). Uniform scale changes neither the axis nor which
// end is wider, so the mesh is only centred. Null where it will not load.
async function shaftAxis(job: Job3D): Promise<{ into: Vec3; even: boolean } | null> {
  const { THREE, GLTFLoader } = await loadDeps();
  const source = await loadFit(new GLTFLoader(), meshSrc(job.id, meshStamp(job)));
  if (!source) return null;
  const obj = source.clone(true);
  obj.position.copy(new THREE.Box3().setFromObject(obj).getCenter(new THREE.Vector3())).multiplyScalar(-1);
  const holder = new THREE.Group();
  holder.add(obj);
  const { into, even } = fitAxis(THREE, holder);
  return { into, even };
}

// A RING ON A SHAFT STOPS WHERE THE SHAFT GETS TOO WIDE FOR ITS HOLE — under the head. Measured on
// the two meshes, along the shaft's axis: the set's own placement is trusted for where the ring is
// across the axis, not along it. A spring washer stood 0.2–0.35 mm inside the head of the screw it
// was built on, and 0.55 mm inside the head of the screw kept from another set, whose box is the
// same and whose head is 0.2 mm taller.
//
// In the shaft's frame (its box's middle), millimetres; `into` from head to tip. Null where it is
// not a ring around this shaft (its hole narrower than the shank, or off the axis by a millimetre),
// or where a mesh will not load — the set's placement then stands as it was.
const SLIDE_MM = 0.05;
async function ringStop(p: SetPlacement, shaftMoves: boolean, into: Vec3): Promise<Vec3 | null> {
  const { THREE, GLTFLoader } = await loadDeps();
  const loader = new GLTFLoader();
  const points = async (job: Job3D): Promise<Vec3[] | null> => {
    const built = builtBoundsOf(job);
    const source = await loadFit(loader, meshSrc(job.id, meshStamp(job)));
    if (!built || !source) return null;
    const obj = source.clone(true);
    obj.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(obj);
    const has = box.getSize(new THREE.Vector3());
    const k = Math.max(...size(built)) / Math.max(has.x, has.y, has.z);
    const c = box.getCenter(new THREE.Vector3());
    const out: Vec3[] = [];
    const v = new THREE.Vector3();
    obj.traverse((o) => {
      const pos = (o as import('three').Mesh).geometry?.getAttribute?.('position');
      if (!pos) return;
      for (let i = 0; i < pos.count; i += 1) {
        v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld);
        out.push([(v.x - c.x) * k, (v.y - c.y) * k, (v.z - c.z) * k]);
      }
    });
    return out;
  };
  const [shaft, ring] = await Promise.all([points(shaftMoves ? p.moving : p.target), points(shaftMoves ? p.target : p.moving)]);
  if (!shaft?.length || !ring?.length) return null;
  const along = (q: Vec3) => q[0] * into[0] + q[1] * into[1] + q[2] * into[2];
  const across = (q: Vec3) => {
    const t = along(q);
    return Math.hypot(q[0] - t * into[0], q[1] - t * into[1], q[2] - t * into[2]);
  };
  // The ring's centre from the shaft's, as the set has it.
  const at: Vec3 = shaftMoves ? flip(p.seat) : p.seat;
  if (across(at) > 1) return null;
  const hole = Math.min(...ring.map(across));
  const half = Math.max(...ring.map((q) => Math.abs(along(q))));
  const length = Math.max(...shaft.map(along)) - Math.min(...shaft.map(along));
  const shank = Math.max(...shaft.filter((q) => Math.abs(along(q)) < length / 10).map(across));
  if (!(hole + SLIDE_MM >= shank)) return null;
  // The head's side is behind the middle: the furthest point toward the tip that is too wide.
  let stop = -Infinity;
  for (const q of shaft) {
    const t = along(q);
    if (t < 0 && t > stop && across(q) > hole + SLIDE_MM) stop = t;
  }
  if (!Number.isFinite(stop)) return null;
  const t = stop + half;
  const d = t - along(at);
  const moved: Vec3 = [at[0] + into[0] * d, at[1] + into[1] * d, at[2] + into[2] * d];
  return shaftMoves ? flip(moved) : moved;
}

// WHICH WAY IT COMES IN, KNOWN AND NOT SEARCHED: along the shaft — the longer of the two, a
// screw's — and from its tip. A washer is threaded on over the tip because the head is in the
// way; a screw is put through a washer tip first. Both are the shaft's axis, and the set already
// says which end of it is the head.
//
// A SHAFT ALIKE AT BOTH ENDS (a dowel) has its two ends played against the meshes, and the one
// along which nothing passes through anything is kept. A shaft whose mesh will not load is an
// error, not a guess: no other way in is tried.
export async function approachFromSet(
  view: AssemblyView,
  fitting: Fitting,
  p: SetPlacement,
  step: number,
): Promise<{ axis: Vec3; travel: number; seat: Vec3 }> {
  const shaftMoves = Math.max(...p.movingSize) > Math.max(...p.targetSize);
  const shaft = await shaftAxis(shaftMoves ? p.moving : p.target).catch(() => null);
  if (!shaft) throw new RuleError('notice.rule.modelNoAxis', { job: (shaftMoves ? p.moving : p.target).id });
  // AND A RING SEATED UNDER THE HEAD, where the shaft has one (see ringStop).
  if (!shaft.even) {
    const seat = await ringStop(p, shaftMoves, shaft.into).catch(() => null);
    if (seat) p = { ...p, seat };
  }
  // From the tip's side: the moving part starts out along the axis it is given, so a washer on a
  // fixed screw starts beyond the tip (+into), and a moving screw behind the washer (-into).
  const way = shaftMoves ? flip(shaft.into) : shaft.into;
  if (!shaft.even) return { axis: way, travel: travelAlong(p, way), seat: p.seat };
  const tried: { axis: Vec3; travel: number; worst: number }[] = [];
  for (const axis of [way, flip(way)]) {
    const travel = travelAlong(p, axis);
    const asm = assemblyOf({
      ...fitting, ...setPose(p), axis, travel, pitch: 0, depth: 0, drive: 'straight', turn: null,
    });
    const profile = await view.clash(asm, step, MOMENTS);
    tried.push({ axis, travel, worst: profile.worst?.mm ?? 0 });
  }
  tried.sort((a, b) => (b.worst - a.worst) || (a.travel - b.travel));
  return { axis: tried[0].axis, travel: tried[0].travel, seat: p.seat };
}

// The rest of the fitting the set decides: both parts square, the fixed one at the origin.
export const setPose = (p: SetPlacement) => ({
  targetSeat: [0, 0, 0] as Vec3,
  targetSpin: [0, 0, 0] as Vec3,
  seat: p.seat,
  spin: [0, 0, 0] as Vec3,
});

// WHERE A TOOL BEARS ON THE PART IT WORKS, from a hardware set that built the two together — the
// PVC screwdriver in the barrel nut, the hex key in the screw's head: the tool's centre from the
// part's, in millimetres, in the part's frame (see Fitting.toolRide). The moving part's own set
// where it holds the tool, else any of the manual's sets that holds both; each kept model must be
// the size its set measured it, as in setPlacement. Null where a hand has put the tool somewhere,
// where no set holds the two, or where either was turned or rebuilt since.
//
// A PIECE MADE EARLIER (the screw with its washer on, 101) is in no set: the tool bears on the part
// of it a set built the tool with, at that part's place in the piece (see PiecePart.m), and the ride
// is said from the piece's own middle. `jobOf`: the kept models by run, to find the piece's parts.
export function toolRideOf(
  f: Pick<Fitting, 'toolSeat' | 'toolSpin'>,
  moving: Job3D | null | undefined,
  tool: Job3D | null | undefined,
  sets: Job3D[],
  jobOf: (id: number) => Job3D | undefined = () => undefined,
): Vec3 | null {
  if (toolPlaced(f) || !moving || !tool) return null;
  const parts = madeOf(moving);
  if (parts) return rideOnPiece(f, parts, tool, sets, jobOf);
  if (shapeEdits(moving) > 0 || shapeEdits(tool) > 0) return null;
  const own = pieceOf(moving)?.of;
  const holding = sets.filter((s) => boxInSet(s, moving) && boxInSet(s, tool));
  const set = holding.find((s) => s.id === own) ?? holding[0];
  if (!set) return null;
  const bm = boxInSet(set, moving)!;
  const bt = boxInSet(set, tool)!;
  const keptM = builtBoundsOf(moving);
  const keptT = builtBoundsOf(tool);
  if (!keptM || !keptT) return null;
  const same = (a: { min: number[]; max: number[] }, b: { min: number[]; max: number[] }) =>
    size(a).every((v, i) => Math.abs(v - size(b)[i]) <= SAME_MM);
  if (!same(bm, keptM) || !same(bt, keptT)) return null;
  const [cm, ct] = [centre(bm), centre(bt)];
  return [ct[0] - cm[0], ct[1] - cm[1], ct[2] - cm[2]];
}

// HOW FAR THE TOOL STANDS FROM A PART in a set that built the two: the room between their boxes,
// in mm, 0 where they touch. A set holding the barrel nut, the JCBC screw and the hex key has the
// key in the screw's head and clear of the nut, and the key is for the screw. Infinity where no set
// holds the two (a piece made earlier among them: it is in none).
export function toolGap(part: Job3D | null | undefined, tool: Job3D | null | undefined, sets: Job3D[]): number {
  if (!part || !tool || madeOf(part)) return Infinity;
  const gaps = sets.map((s) => {
    const [a, b] = [boxInSet(s, part), boxInSet(s, tool)];
    if (!a || !b) return Infinity;
    return Math.hypot(...[0, 1, 2].map((i) => Math.max(0, a.min[i] - b.max[i], b.min[i] - a.max[i])));
  });
  return Math.min(Infinity, ...gaps);
}
